import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Headphones,
  ShieldAlert,
  ScrollText,
  Cog,
  Workflow,
  Wallet,
  BookOpen,
  Brain,
  Boxes,
  Building2,
  Server,
  LogOut,
  Shield,
  Gavel,
  Heart,
  FileCheck2,
  Receipt,
  Plug,
  ArrowLeftRight,
  Network,
  KeyRound,
  Activity,
  UsersRound,
  MessageSquare,
  HeartHandshake,
  FileText,
  Waypoints,
  Gauge,
  Radar,
  Handshake
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import clsx from 'clsx';

/**
 * Admin sidebar — every staff-only view in one place. Items split into
 * three sections: operational (daily use), accounting/governance, and
 * platform (infra-side). Each link is either a built screen or a
 * migration-pending link that opens the equivalent on app.nurev.io
 * until ported.
 */
const NAV_OPS = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/customer-service', label: 'Customer Service', icon: Headphones },
  { to: '/escalations', label: 'Escalations', icon: ShieldAlert },
  { to: '/feedback', label: 'Feedback', icon: MessageSquare },
  { to: '/retention', label: 'Retention', icon: HeartHandshake },
  { to: '/audit-log', label: 'Audit Log', icon: ScrollText },
  { to: '/pipeline', label: 'Pipeline', icon: Workflow },
  { to: '/discord-prospects', label: 'Discord Prospects', icon: Radar },
  { to: '/bot-partners', label: 'Bot Partners', icon: Handshake },
  { to: '/reward-caps', label: 'Reward Caps', icon: Gauge },
  // ─── User-management items deep-link back to app.nurev.io until
  //     ported. Roles are gated server-side, so no risk of leakage.
  { to: '/admin-users', label: 'Admins', icon: UsersRound },
  { to: '/publishers', label: 'Publishers', icon: UsersRound },
  { to: '/super-creators', label: 'Super Creators', icon: UsersRound },
  { to: '/charity-verify', label: 'Charity Verify', icon: Heart }
];

const NAV_BIZ = [
  { to: '/crypto-assets', label: 'Crypto Assets', icon: Wallet },
  { to: '/cap-table', label: 'Cap Table', icon: BookOpen },
  { to: '/inference', label: 'Inference Admin', icon: Brain },
  { to: '/board', label: 'Board Meetings', icon: Gavel },
  { to: '/signing-approvals', label: 'Signing Approvals', icon: FileCheck2 },
  { to: '/server-billing', label: 'Server Billing', icon: Receipt }
];

const NAV_INFRA = [
  { to: '/system-map', label: 'System Map', icon: Waypoints },
  { to: '/infrastructure', label: 'Infrastructure', icon: Server },
  { to: '/components', label: 'Components', icon: Boxes },
  { to: '/integrations', label: 'Integrations', icon: Building2 },
  { to: '/services', label: 'Third-party Services', icon: Plug },
  { to: '/exchange-routers', label: 'Exchange Routers', icon: ArrowLeftRight },
  { to: '/networks', label: 'Networks', icon: Network },
  { to: '/code-signing', label: 'Code Signing & AV', icon: KeyRound },
  { to: '/k8s-health', label: 'K8s Health', icon: Activity },
  { to: '/eula', label: 'EULA', icon: FileText }
];

export function MainLayout() {
  const navigate = useNavigate();
  const admin = useAuthStore(s => s.admin);
  const clear = useAuthStore(s => s.clear);

  const logout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-nurev-bg">
      <aside className="flex w-64 flex-col border-r border-nurev-border bg-nurev-surface px-4 py-6">
        <div className="mb-8 flex items-center gap-3 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30">
            <Shield className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-wide text-nurev-text">
              NUREV
            </div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400">
              Admin Console
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto">
          <NavGroup title="Operational" items={NAV_OPS} />
          <NavGroup title="Business" items={NAV_BIZ} />
          <NavGroup title="Platform" items={NAV_INFRA} />
        </nav>

        <div className="mt-6 border-t border-nurev-border pt-4">
          {admin && (
            <div className="mb-2 px-3 text-xs">
              <div className="font-medium text-nurev-text">{admin.name}</div>
              <div className="truncate text-nurev-muted">{admin.email}</div>
              <div className="mt-0.5 text-amber-400">{admin.role}</div>
            </div>
          )}
          <button onClick={logout} className="btn-ghost w-full justify-start">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-8 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavGroup({
  title,
  items
}: {
  title: string;
  items: Array<{
    to: string;
    label: string;
    icon: typeof Cog;
    end?: boolean;
  }>;
}) {
  return (
    <div>
      <div className="mb-1.5 px-3 text-[10px] font-medium uppercase tracking-widest text-nurev-muted">
        {title}
      </div>
      <div className="space-y-1">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
                isActive
                  ? 'bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/30'
                  : 'text-nurev-text-soft hover:bg-nurev-surface-lift hover:text-nurev-text'
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
